import { useReducedMotion } from 'motion/react';
import { memo, type ReactNode, useEffect, useRef } from 'react';

import { Button } from '@air/primitive-button';

import { WorkspaceAgentPanelZeroStateSuggestionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/WorkspaceAgentPanelZeroStateSuggestionRow';
import { type WorkspaceAgentBoardTemplateOption } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentBoardTemplates';
import { WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_DURATION_S } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentZeroStateEntrance';

const CHOICE_SELECTOR = 'button:not(:disabled)';

export type WorkspaceAgentBoardTemplatesFlowProps = {
  boardChip: ReactNode;
  boardTitle: string;
  isDisabled: boolean;
  onCancel: () => void;
  onSelect: (optionId: string) => void;
  options: readonly Pick<WorkspaceAgentBoardTemplateOption, 'icon' | 'id' | 'title'>[];
};

/** The empty thread a board's "Board templates" menu item opens: pick a starting point instead of reading a prompt. */
export const WorkspaceAgentBoardTemplatesFlow = memo(
  ({ boardChip, boardTitle, isDisabled, onCancel, onSelect, options }: WorkspaceAgentBoardTemplatesFlowProps) => {
    const choicesRef = useRef<HTMLFieldSetElement>(null);
    const shouldReduceMotion = useReducedMotion();
    const transitionDuration = shouldReduceMotion ? 0 : WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_DURATION_S;

    useEffect(() => {
      const choices = choicesRef.current;

      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React on mount; Biome only sees the initial `null`.
      if (!choices) {
        return;
      }

      choices.querySelector<HTMLElement>(CHOICE_SELECTOR)?.focus({ preventScroll: true });

      const handleKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
          return;
        }

        const enabledChoices = Array.from(choices.querySelectorAll<HTMLElement>(CHOICE_SELECTOR));

        if (enabledChoices.length === 0) {
          return;
        }

        event.preventDefault();

        const currentIndex = enabledChoices.findIndex((choice) => choice.matches(':focus'));
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex =
          currentIndex === -1 ? 0 : (currentIndex + step + enabledChoices.length) % enabledChoices.length;

        enabledChoices[nextIndex]?.focus();
      };

      choices.addEventListener('keydown', handleKeyDown);

      return () => {
        choices.removeEventListener('keydown', handleKeyDown);
      };
    }, []);

    return (
      <div className="flex flex-col gap-4" data-testid="WORKSPACE_AGENT_BOARD_TEMPLATES_FLOW">
        <p
          className="ads:text-primary text-14 text-gray-12 leading-5"
          data-testid="WORKSPACE_AGENT_BOARD_TEMPLATES_QUESTION"
        >
          How do you want to set up the sub-boards in {boardChip}?
        </p>
        <fieldset
          aria-label={`How do you want to set up the sub-boards in ${boardTitle}?`}
          className="m-0 flex min-w-0 flex-col items-start gap-2 overflow-visible border-0 px-0 py-0.5"
          data-testid="WORKSPACE_AGENT_BOARD_TEMPLATES_CHOICES"
          ref={choicesRef}
        >
          {options.map((option, index) => (
            <WorkspaceAgentPanelZeroStateSuggestionRow
              index={index}
              isDisabled={isDisabled}
              isVisible
              key={option.id}
              onSelect={onSelect}
              showQuickReplyShimmer={false}
              slotCount={options.length}
              suggestion={option}
              transitionDuration={transitionDuration}
            />
          ))}
        </fieldset>
        <Button
          appearance="ghost"
          className="self-start"
          color="gray"
          data-testid="WORKSPACE_AGENT_BOARD_TEMPLATES_CANCEL"
          onClick={onCancel}
          size="small"
        >
          Cancel
        </Button>
      </div>
    );
  },
);

WorkspaceAgentBoardTemplatesFlow.displayName = 'WorkspaceAgentBoardTemplatesFlow';
